import { useState, useEffect, ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  Search, X, User, Bell, Shield, AtSign, Users as UsersIcon,
  Loader, Check, Plug, ChevronRight, ArrowLeft, Clock,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { getErrorMessage } from "../lib/api";
import UserManagement from "./UserManagement";
import AuditLog from "./AuditLog";
import Avatar from "./common/Avatar";
import IOSToggle from "./common/IOSToggle";
import Button from "./common/Button";

type Tab = "profil" | "varsler" | "sikkerhet" | "epost" | "erp" | "brukere" | "aktivitet";

// ── Layout-byggesteiner ────────────────────────────────────────────────
function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-9">
      <h3 className="text-[15px] font-bold text-night mb-1">{title}</h3>
      <div>{children}</div>
    </div>
  );
}

function Row({ label, hint, children, last }: { label: string; hint?: string; children?: ReactNode; last?: boolean }) {
  return (
    <div className={`flex items-center justify-between gap-6 py-3.5 ${last ? "" : "border-b border-line"}`}>
      <div className="min-w-0">
        <p className="text-[14px] font-medium text-night">{label}</p>
        {hint && <p className="text-[12.5px] text-muted mt-0.5">{hint}</p>}
      </div>
      <div className="shrink-0 flex items-center gap-2">{children}</div>
    </div>
  );
}

// ── Profil ─────────────────────────────────────────────────────────────
function ProfileSection({ user, onUserChange, onUserUpdate }: { user: any; onUserChange: (u: any) => void; onUserUpdate?: (u: any) => void }) {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState(user?.full_name || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const roleLabel = user?.role === "admin" ? t("roles.admin") : user?.role === "cfo" ? t("roles.cfo")
    : user?.role === "project_manager" ? t("roles.project_manager") : t("roles.site_manager");

  const dirty = name.trim() !== (user?.full_name || "") && name.trim().length >= 2;

  const saveName = async () => {
    if (!dirty) return;
    setSaving(true); setSaved(false);
    try {
      const r = await api.patch("/users/me", { full_name: name.trim() });
      const updated = { ...user, full_name: r.data.full_name };
      localStorage.setItem("contrack_user", JSON.stringify(updated));
      onUserChange(updated);
      onUserUpdate?.(updated);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  const setLang = (lng: string) => { i18n.changeLanguage(lng); localStorage.setItem("contrack_lang", lng); };
  const isEN = i18n.language?.startsWith("en");

  return (
    <>
      <Group title={t("settings.profil")}>
        <Row label={t("settings.avatar")}>
          <Avatar name={user?.full_name || "?"} size="md" />
        </Row>
        <Row label={t("settings.navn")}>
          <div className="flex items-center gap-2">
            <input value={name} onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") saveName(); }}
              className="w-56 max-w-[55vw] bg-canvas border border-line rounded-[9px] px-3 py-1.5 text-[13.5px] text-night focus:bg-white focus:border-night/25 focus:outline-none transition-all" />
            {dirty && (
              <button onClick={saveName} disabled={saving} className="cx-btn-dark !py-1.5 !px-3 !text-[12.5px]">
                {saving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : t("common.lagre")}
              </button>
            )}
            {saved && <Check className="w-4 h-4 text-pos" />}
          </div>
        </Row>
        <Row label={t("settings.epost_label")}>
          <span className="text-[13.5px] text-muted">{user?.email}</span>
        </Row>
        <Row label={t("settings.selskap")}>
          <span className="text-[13.5px] text-muted">{user?.company || "—"}</span>
        </Row>
        <Row label={t("settings.rolle")} last>
          <span className="cx-chip bg-black/[0.05] text-muted">{roleLabel}</span>
        </Row>
      </Group>

      <Group title={t("settings.preferanser")}>
        <Row label={t("settings.sprak")} hint={t("settings.sprak_hint")} last>
          <div className="flex bg-canvas border border-line rounded-[9px] p-0.5">
            <button onClick={() => setLang("nb")}
              className={`px-3 py-1 rounded-[6px] text-[12px] font-bold transition-all ${!isEN ? "bg-white text-night shadow-sm" : "text-faint"}`}>NO</button>
            <button onClick={() => setLang("en")}
              className={`px-3 py-1 rounded-[6px] text-[12px] font-bold transition-all ${isEN ? "bg-white text-night shadow-sm" : "text-faint"}`}>EN</button>
          </div>
        </Row>
      </Group>
    </>
  );
}

// ── Varsler ────────────────────────────────────────────────────────────
function NotificationsSection() {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/users/me").then(r => setEnabled(r.data.email_notifications_enabled)).catch(() => setEnabled(true));
  }, []);

  const toggle = async (next: boolean) => {
    setSaving(true);
    try {
      const res = await api.patch("/users/me/notification-prefs", { email_notifications_enabled: next });
      setEnabled(res.data.email_notifications_enabled);
    } catch {} finally { setSaving(false); }
  };

  return (
    <Group title={t("settings.varsler")}>
      <Row label={t("settings.epostvarsler")} hint={t("settings.epostvarsler_sub")}>
        {saving && <Loader className="w-3.5 h-3.5 animate-spin text-faint" />}
        <IOSToggle checked={enabled === true} onChange={toggle} disabled={saving || enabled === null} />
      </Row>
      <Row label={t("settings.i_app_varsler")} hint={t("settings.i_app_sub")} last>
        <span className="text-[13px] text-muted">{t("settings.alltid_pa")}</span>
      </Row>
    </Group>
  );
}

// ── Sikkerhet (passord + MFA + økter) ─────────────────────────────────
function SecuritySection() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ current: "", newPw: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);

  const handleSubmit = async () => {
    if (form.newPw !== form.confirm) { setMessage({ text: t("settings.passord_mismatch"), ok: false }); return; }
    setLoading(true); setMessage(null);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.newPw });
      setMessage({ text: t("settings.passord_endret"), ok: true });
      setForm({ current: "", newPw: "", confirm: "" });
    } catch (e: any) {
      setMessage({ text: getErrorMessage(e, "Feil ved passordendring"), ok: false });
    } finally { setLoading(false); }
  };

  return (
    <>
      <Group title={t("settings.passord")}>
        <div className="space-y-3 pt-1">
          <input type="password" placeholder={t("settings.navarende_passord")} value={form.current}
            onChange={e => setForm({ ...form, current: e.target.value })} className="field-ios" />
          <input type="password" placeholder={t("settings.nytt_passord")} value={form.newPw}
            onChange={e => setForm({ ...form, newPw: e.target.value })} className="field-ios" />
          <input type="password" placeholder={t("settings.bekreft_passord")} value={form.confirm}
            onChange={e => setForm({ ...form, confirm: e.target.value })}
            onKeyDown={e => e.key === "Enter" && handleSubmit()} className="field-ios" />
        </div>
        {message && (
          <div className={`rounded-[10px] p-3 text-[13px] mt-3 ${message.ok ? "bg-pos/10 text-pos" : "bg-neg/10 text-neg"}`}>{message.text}</div>
        )}
        <div className="mt-4">
          <Button variant="primary" loading={loading} onClick={handleSubmit}
            disabled={loading || !form.current || !form.newPw || !form.confirm}>
            {t("settings.endre_passord")}
          </Button>
        </div>
      </Group>
      <MFASection />
      <SessionsSection />
    </>
  );
}

function MFASection() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<any>(null);
  const [showSetup, setShowSetup] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [disableForm, setDisableForm] = useState({ password: "", code: "", show: false });

  const fetchStatus = async () => { try { const r = await api.get("/auth/mfa/status"); setStatus(r.data); } catch {} };
  useEffect(() => { fetchStatus(); }, []);

  const disable = async () => {
    setDisabling(true);
    try { await api.post("/auth/mfa/disable", disableForm); setDisableForm({ password: "", code: "", show: false }); fetchStatus(); }
    catch (e: any) { alert(getErrorMessage(e, "Feil")); } finally { setDisabling(false); }
  };

  if (showSetup) {
    const MFASetup = require("./MFASetup").default;
    return <Group title={t("settings.totrinnsverifisering")}><MFASetup onComplete={() => { setShowSetup(false); fetchStatus(); }} onCancel={() => setShowSetup(false)} /></Group>;
  }

  const enabled = status?.enabled;
  const required = status?.required;

  return (
    <Group title={t("settings.totrinnsverifisering")}>
      <Row label={t("settings.totrinnsverifisering")}
        hint={enabled ? t("mfa.status_active", { count: status?.backup_codes_remaining ?? 0 }) : required ? t("mfa.required_note") : t("settings.totrinn_sub")}
        last={!disableForm.show}>
        {enabled ? (
          <>
            <span className="cx-chip bg-pos/10 text-pos">{t("settings.aktiv")}</span>
            {!required && (
              <button onClick={() => setDisableForm({ ...disableForm, show: true })}
                className="text-[13px] font-semibold text-neg px-2 py-1 hover:bg-neg/10 rounded-lg">{t("mfa.disable")}</button>
            )}
          </>
        ) : (
          <Button variant="secondary" onClick={() => setShowSetup(true)}>{t("mfa.setup")}</Button>
        )}
      </Row>
      {disableForm.show && !required && (
        <div className="py-4 space-y-2">
          <p className="text-[12.5px] text-muted">{t("mfa.disable_warning")}</p>
          <input type="password" placeholder={t("settings.navarende_passord")} value={disableForm.password}
            onChange={e => setDisableForm({ ...disableForm, password: e.target.value })} className="field-ios !py-2 !text-[13px]" />
          <input placeholder={t("auth.mfa_code")} value={disableForm.code}
            onChange={e => setDisableForm({ ...disableForm, code: e.target.value.toUpperCase() })} className="field-ios !py-2 !text-[13px] tabular" />
          <div className="flex gap-2">
            <Button variant="danger-solid" loading={disabling} onClick={disable} disabled={!disableForm.password || !disableForm.code}>{t("mfa.confirm_disable")}</Button>
            <Button variant="neutral" onClick={() => setDisableForm({ password: "", code: "", show: false })}>{t("common.avbryt")}</Button>
          </div>
        </div>
      )}
    </Group>
  );
}

function SessionsSection() {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<any[]>([]);
  const [revoking, setRevoking] = useState<string | null>(null);

  const fetchSessions = async () => { try { const r = await api.get("/auth/sessions"); setSessions(r.data.sessions || []); } catch {} };
  useEffect(() => { fetchSessions(); }, []);

  const revoke = async (id: string) => {
    setRevoking(id);
    try { await api.post(`/auth/sessions/${id}/revoke`); fetchSessions(); } catch {} finally { setRevoking(null); }
  };

  return (
    <Group title={t("sessions.title")}>
      {sessions.length === 0 ? (
        <p className="text-[13px] text-faint py-2">{t("sessions.none")}</p>
      ) : sessions.map((s, i) => (
        <Row key={s.id} label={s.device}
          hint={`${s.ip_address} · ${s.created_at?.slice(0, 16).replace("T", " ")}`}
          last={i === sessions.length - 1}>
          {s.is_current ? (
            <span className="text-[11px] font-bold uppercase text-pos">{t("sessions.current")}</span>
          ) : (
            <button onClick={() => revoke(s.id)} disabled={revoking === s.id}
              className="text-[13px] font-semibold text-neg px-2 py-1 hover:bg-neg/10 rounded-lg">
              {revoking === s.id ? "..." : t("sessions.revoke")}
            </button>
          )}
        </Row>
      ))}
    </Group>
  );
}

// ── E-post ─────────────────────────────────────────────────────────────
function EmailSection() {
  const { t } = useTranslation();
  const [connections, setConnections] = useState<any[]>([]);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const isAdmin = JSON.parse(localStorage.getItem("contrack_user") || "null")?.role === "admin";

  const fetchStatus = async () => {
    try { const res = await api.get("/auth/email/status"); setConnections(res.data.connections); }
    catch (e: any) { console.error("E-poststatus feilet:", e?.response?.data ?? e); }
  };

  useEffect(() => {
    fetchStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get("email_connected")) { fetchStatus(); window.history.replaceState({}, "", window.location.pathname); }
    const err = params.get("email_error");
    if (err) {
      const code = params.get("code") || ""; const detail = params.get("detail") || "";
      alert(`E-postkobling feilet (${err}${code ? ": " + code : ""})${detail ? "\n\n" + detail : ""}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connectGmail = async () => { try { const res = await api.get("/auth/email/gmail/connect"); window.location.href = res.data.auth_url; } catch (err: any) { alert(`Gmail: ${err?.response?.data?.detail ?? "ukjent feil"}`); } };
  const connectOutlook = async () => { try { const res = await api.get("/auth/email/outlook/connect"); window.location.href = res.data.auth_url; } catch (err: any) { alert(`Outlook: ${err?.response?.data?.detail ?? "ukjent feil"}`); } };
  const disconnect = async (id: string) => { setDisconnecting(id); try { await api.post(`/auth/email/disconnect/${id}`); fetchStatus(); } catch {} finally { setDisconnecting(null); } };

  const gmailConn = connections.find(c => c.provider === "gmail");
  const outlookConn = connections.find(c => c.provider === "outlook");

  const row = (label: string, conn: any, onConnect: () => void, last = false) => (
    <Row label={label} hint={conn ? conn.email_address : t("common.ikke_tilkoblet")} last={last}>
      {conn ? (
        <>
          {conn.status === "expired" && <span className="cx-chip bg-flag/10 text-flag">{t("settings.utlopt")}</span>}
          {conn.status === "active" && <span className="cx-chip bg-pos/10 text-pos">{t("settings.aktiv")}</span>}
          {isAdmin && conn.status === "expired" && <Button variant="secondary" onClick={onConnect}>{t("settings.koble_til_paa_nytt")}</Button>}
          {isAdmin && (
            <button onClick={() => disconnect(conn.id)} disabled={disconnecting === conn.id}
              className="text-[13px] font-semibold text-neg px-2 py-1 hover:bg-neg/10 rounded-lg transition-colors">
              {disconnecting === conn.id ? <Loader className="w-3.5 h-3.5 animate-spin" /> : t("settings.frakoble")}
            </button>
          )}
        </>
      ) : (
        isAdmin ? <Button variant="secondary" onClick={onConnect}>{t("settings.koble_til")}</Button>
          : <span className="text-[12px] text-faint">{t("settings.kontakt_admin_epost")}</span>
      )}
    </Row>
  );

  return (
    <Group title={t("settings.epostintegrasjon")}>
      <p className="text-[12.5px] text-muted mb-2 -mt-0.5">{t("settings.epost_sub")}</p>
      {row(t("settings.google_workspace"), gmailConn, connectGmail)}
      {row(t("settings.outlook_365"), outlookConn, connectOutlook, true)}
    </Group>
  );
}

// ── ERP-integrasjoner ──────────────────────────────────────────────────
const ERP_PROVIDERS: { id: string; name: string; short: string; color: string; available: boolean }[] = [
  { id: "tripletex", name: "Tripletex", short: "Tx", color: "#16A34A", available: true },
  { id: "visma", name: "Visma Net", short: "V", color: "#F97316", available: false },
  { id: "dynamics", name: "MS Dynamics 365", short: "D", color: "#2563EB", available: false },
  { id: "poweroffice", name: "Power Office", short: "P", color: "#DC2626", available: false },
  { id: "sevenoffice", name: "24SevenOffice", short: "24", color: "#52525B", available: false },
];

function ProviderTile({ short, color }: { short: string; color: string }) {
  return (
    <span className="w-11 h-11 rounded-[12px] border border-line bg-canvas flex items-center justify-center shrink-0"
      style={{ color }}>
      <span className="text-[13px] font-bold tabular">{short}</span>
    </span>
  );
}

// Tripletex-detalj: tilkobling + synkronisering
function TripletexDetail({ onBack, conn, onChanged }: { onBack: () => void; conn: any; onChanged: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ env: "test", base_url: "https://api-test.tripletex.tech", company_ref: "0", employee_token: "" });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);

  const connect = async () => {
    setSaving(true); setErr(null);
    try { await api.post("/erp/connect", form); setForm({ ...form, employee_token: "" }); onChanged(); }
    catch (e: any) { setErr(getErrorMessage(e, "Tilkobling feilet")); } finally { setSaving(false); }
  };
  const disconnect = async () => { try { await api.post("/erp/disconnect"); setSyncResult(null); onChanged(); } catch {} };
  const sync = async () => {
    setSyncing(true); setSyncResult(null); setErr(null);
    try { const r = await api.post("/erp/sync"); setSyncResult(r.data); }
    catch (e: any) { setErr(getErrorMessage(e, "Synkronisering feilet")); } finally { setSyncing(false); }
  };

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-muted hover:text-night mb-3">
        <ArrowLeft className="w-4 h-4" /> {t("settings.erp_alle")}
      </button>
      <div className="flex items-center gap-3 mb-4">
        <ProviderTile short="Tx" color="#16A34A" />
        <div>
          <p className="text-[15px] font-bold text-night">Tripletex</p>
          <p className="text-[12.5px] text-muted">{t("settings.erp_sub")}</p>
        </div>
      </div>
      {conn?.connected ? (
        <>
          <Row label={t("settings.erp_tilkobling")} hint={`${conn.env} · ${conn.base_url}`}>
            <span className="cx-chip bg-pos/10 text-pos">{t("settings.aktiv")}</span>
            <button onClick={disconnect} className="text-[13px] font-semibold text-neg px-2 py-1 hover:bg-neg/10 rounded-lg transition-colors">{t("settings.frakoble")}</button>
          </Row>
          <Row label={t("settings.erp_sync")} hint={conn.last_sync_at ? new Date(conn.last_sync_at).toLocaleString("nb-NO") : t("settings.erp_aldri_synket")} last>
            <Button variant="secondary" loading={syncing} onClick={sync}>{t("settings.erp_synkroniser")}</Button>
          </Row>
          {syncResult && (
            <p className="text-[12.5px] text-muted mt-2">
              {t("settings.erp_sync_resultat", { created: syncResult.created, linked: syncResult.linked, queue: syncResult.queue, skipped: syncResult.skipped })}
            </p>
          )}
        </>
      ) : (
        <div className="space-y-2.5">
          <select value={form.env} onChange={e => setForm({ ...form, env: e.target.value, base_url: e.target.value === "test" ? "https://api-test.tripletex.tech" : "https://tripletex.no" })} className="field-ios">
            <option value="test">Test (api-test.tripletex.tech)</option>
            <option value="prod">Prod (tripletex.no)</option>
          </select>
          <input value={form.base_url} onChange={e => setForm({ ...form, base_url: e.target.value })} placeholder="Base-URL" className="field-ios" />
          <input value={form.company_ref} onChange={e => setForm({ ...form, company_ref: e.target.value })} placeholder={t("settings.erp_company_ref")} className="field-ios" />
          <input type="password" value={form.employee_token} onChange={e => setForm({ ...form, employee_token: e.target.value })} placeholder={t("settings.erp_employee_token")} className="field-ios" />
          <Button variant="primary" loading={saving} onClick={connect} disabled={!form.employee_token}>{t("settings.koble_til")}</Button>
        </div>
      )}
      {err && <p className="text-[12.5px] text-neg mt-2">{err}</p>}
    </div>
  );
}

function ErpSection() {
  const { t } = useTranslation();
  const isAdmin = JSON.parse(localStorage.getItem("contrack_user") || "null")?.role === "admin";
  const [conn, setConn] = useState<any>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const fetchStatus = async () => { try { const r = await api.get("/erp/status"); setConn(r.data.connection); } catch {} };
  useEffect(() => { fetchStatus(); }, []);

  if (!isAdmin) return (
    <Group title={t("settings.erp_integrasjon")}>
      <p className="text-[12.5px] text-muted">{t("settings.kontakt_admin_epost")}</p>
    </Group>
  );

  if (selected === "tripletex") {
    return (
      <Group title={t("settings.erp_integrasjon")}>
        <TripletexDetail conn={conn} onBack={() => setSelected(null)} onChanged={fetchStatus} />
      </Group>
    );
  }

  return (
    <Group title={t("settings.erp_integrasjon")}>
      <p className="text-[12.5px] text-muted mb-3 -mt-0.5">{t("settings.erp_velg")}</p>
      <div className="cx-card overflow-hidden">
        {ERP_PROVIDERS.map((p, i) => {
          const connected = p.id === "tripletex" && conn?.connected;
          return (
            <button key={p.id} disabled={!p.available} onClick={() => p.available && setSelected(p.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${i > 0 ? "border-t border-line" : ""} ${p.available ? "hover:bg-black/[0.02]" : "opacity-45 cursor-not-allowed"}`}>
              <ProviderTile short={p.short} color={p.color} />
              <span className="flex-1 text-[14px] font-medium text-night">{p.name}</span>
              {connected && <span className="cx-chip bg-pos/10 text-pos">{t("settings.aktiv")}</span>}
              {p.available ? <ChevronRight className="w-4 h-4 text-faint shrink-0" />
                : <span className="text-[11.5px] font-medium text-faint shrink-0">{t("settings.erp_kommer")}</span>}
            </button>
          );
        })}
        <div className="flex items-center gap-3 px-4 py-3 border-t border-line opacity-45">
          <span className="w-11 h-11 rounded-[12px] border border-dashed border-line flex items-center justify-center shrink-0 text-faint">+</span>
          <span className="flex-1 text-[14px] font-medium text-muted">{t("settings.erp_flere")}</span>
          <span className="text-[12px] text-faint shrink-0">+12</span>
        </div>
      </div>
    </Group>
  );
}

// ── Modal-skall ────────────────────────────────────────────────────────
export default function SettingsModal({ open, onClose, defaultTab, onUserUpdate }: { open: boolean; onClose: () => void; defaultTab?: string; onUserUpdate?: (u: any) => void }) {
  const { t } = useTranslation();
  const [user, setUser] = useState<any>(JSON.parse(localStorage.getItem("contrack_user") || "null"));
  const [tab, setTab] = useState<Tab>((defaultTab as Tab) || "profil");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setUser(JSON.parse(localStorage.getItem("contrack_user") || "null"));
    if (defaultTab) setTab(defaultTab as Tab);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose, defaultTab]);

  if (!open) return null;

  const isAdmin = user?.role === "admin";
  const isAdminCfo = user?.role === "admin" || user?.role === "cfo";
  const navItems: { id: Tab; label: string; icon: any }[] = [
    { id: "profil", label: t("settings.profil"), icon: User },
    { id: "varsler", label: t("settings.varsler"), icon: Bell },
    { id: "sikkerhet", label: t("settings.sikkerhet_section"), icon: Shield },
    { id: "epost", label: t("settings.epostkobling"), icon: AtSign },
    { id: "erp", label: t("settings.erp_integrasjon"), icon: Plug },
    ...(isAdmin ? [{ id: "brukere" as Tab, label: t("settings.brukere"), icon: UsersIcon }] : []),
    ...(isAdminCfo ? [{ id: "aktivitet" as Tab, label: t("nav.aktivitetslogg"), icon: Clock }] : []),
  ];
  const filtered = query.trim()
    ? navItems.filter(n => n.label.toLowerCase().includes(query.toLowerCase()))
    : navItems;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] animate-backdrop-in" />
      <div onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl h-[80vh] max-h-[680px] bg-card rounded-[18px] shadow-pop border border-line flex overflow-hidden animate-fade-up">

        {/* Venstre nav */}
        <aside className="w-[240px] shrink-0 border-r border-line bg-panel flex flex-col p-3">
          <div className="relative mb-3">
            <Search className="w-3.5 h-3.5 text-faint absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder={t("common.sok")}
              className="w-full pl-8 pr-3 py-2 bg-canvas border border-line rounded-[9px] text-[13px] text-night placeholder:text-faint focus:bg-white focus:border-night/20 focus:outline-none transition-all" />
          </div>
          <p className="cx-section mb-1.5">{t("settings.innstillinger")}</p>
          <nav className="space-y-0.5 overflow-y-auto flex-1">
            {filtered.map(n => {
              const Icon = n.icon;
              const active = tab === n.id;
              return (
                <button key={n.id} onClick={() => setTab(n.id)} className={`cx-nav w-full ${active ? "cx-nav-active" : ""}`}>
                  <Icon className={`w-[17px] h-[17px] shrink-0 ${active ? "text-night" : "text-faint"}`} />
                  <span className="flex-1 text-left">{n.label}</span>
                </button>
              );
            })}
            {filtered.length === 0 && <p className="text-[12.5px] text-faint px-2.5 py-2">{t("common.ingen_data")}</p>}
          </nav>
        </aside>

        {/* Innhold */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="flex items-center justify-end px-6 pt-4">
            <button onClick={onClose} className="p-1.5 rounded-[9px] text-faint hover:text-night hover:bg-black/[0.05] transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="px-7 pb-8 -mt-2 max-w-[640px]">
            {tab === "profil" && <ProfileSection user={user} onUserChange={setUser} onUserUpdate={onUserUpdate} />}
            {tab === "varsler" && <NotificationsSection />}
            {tab === "sikkerhet" && <SecuritySection />}
            {tab === "epost" && <EmailSection />}
            {tab === "erp" && <ErpSection />}
            {tab === "brukere" && (isAdmin
              ? <Group title={t("settings.brukere")}><UserManagement currentUserId={user?.id || ""} /></Group>
              : <p className="text-[13px] text-muted py-8 text-center">{t("settings.kun_admin")}</p>)}
            {tab === "aktivitet" && (isAdminCfo
              ? <AuditLog />
              : <p className="text-[13px] text-muted py-8 text-center">{t("settings.kun_admin")}</p>)}
          </div>
        </main>
      </div>
    </div>,
    document.body
  );
}
